import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogContent,
  FormHelperText,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import "../../Style/dialogstyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { BoxTextField } from "../../Utils/BoxTextField";
import { DialogTitleComp } from "../../Utils/Component";
import { SelectDatePicker } from "../Include/SelectDatePicker";
import { useGetNextNumberingMemberInMandate } from "../../Hooks/useGetNextNumberingMemberInMandate";
import { CREATE_MEMBER_IN_MANDATE, UPDATE_MEMBER_IN_MANDATE } from "../../Schema/Member";
import { SelectParty, SelectCouncilPositions, SelectCandidateInMandate } from "../Include/DynamicSelect";
import moment from "moment";

export default function MemberInMandateForm({
  open,
  editData,
  setRefetch,
  handleClose,
  dialogTitle,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [createMemberInMandate] = useMutation(CREATE_MEMBER_IN_MANDATE, {
    onCompleted: ({ createMemberInMandate }) => {
      setLoading(false);
      if (createMemberInMandate?.isSuccess === true) {
        setAlert(true, "success", createMemberInMandate?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createMemberInMandate?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateMemberInMandate] = useMutation(UPDATE_MEMBER_IN_MANDATE, {
    onCompleted: ({ updateMemberInMandate }) => {
      setLoading(false);
      if (updateMemberInMandate?.isSuccess === true) {
        setAlert(true, "success", updateMemberInMandate?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateMemberInMandate?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    numbering: Yup.number().nullable().required(t("required")),
    startDate: Yup.string().nullable().required(t("required")),
    fromParty: Yup.object().shape({
      id: Yup.string(),
      title: Yup.string(),
    }),
    memberId: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
    positions: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      startDate: moment(),
      numbering: null,
      memberId: { id: "", title: "" },
      positions: { id: "", title: "" },
      fromParty: { id: "", title: "" },
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {

      setLoading(true);
      const inputData = {
        numbering: parseFloat(values?.numbering),
        startDate: moment(values?.startDate).format("YYYY-MM-DD"),
        memberId: values?.memberId?.id,
        positions: values?.positions?.id,
        fromParty: values?.fromParty?.id,
        candidateId: values?.memberId?.candidateId,
      }
    

      if (dialogTitle === "Update") {
        updateMemberInMandate({
          variables: {
            id: editData?._id,
            input: inputData,
          },
        });
      } else {
        createMemberInMandate({
          variables: {
            input: inputData,
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  const { numbering, refetch } = useGetNextNumberingMemberInMandate();

  useEffect(() => {
    refetch();
    if (dialogTitle === "Create") {
      setFieldValue("numbering", numbering);
    }
  }, [dialogTitle, open]);

  useEffect(() => {
    if (editData) {
      setFieldValue("numbering", editData?.numbering);
      setFieldValue("startDate", editData?.startDate);

      setFieldValue("memberId", {
        id: editData?.memberId?._id,
        numberOrder: editData?.candidateId?.numberOrder,
        candidateId: editData?.candidateId?._id,
        title: editData?.memberId?.lastNameKh + " " + editData?.memberId?.firstNameKh,
      });
      setFieldValue("fromParty", {
        id: editData?.fromParty?._id,
        title: editData?.fromParty?.partyName,
      });
      setFieldValue("positions", {
        id: editData?.positions?._id,
        title: editData?.positions?.positionsNameKh,
      });
    }
  }, [editData, open]);


  return (
    <Dialog open={open} className="dialog-container" maxWidth="xs" fullWidth>
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_member_in_mandate")
            : t("modal_title_update_member_in_mandate")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_numbering")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  className="text-field"
                  placeholder={t("thead_numbering")}
                  {...getFieldProps("numbering")}
                  error={Boolean(touched.numbering && errors.numbering)}
                  helperText={touched.numbering && errors.numbering}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_number_order")}
                </Typography>
                <BoxTextField value={values?.memberId?.numberOrder || null} />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_start_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.startDate}
                  onChange={(val) => setFieldValue("startDate", val)}
                />
                {!!errors?.startDate && touched?.startDate && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors?.startDate}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12}>
                <SelectCandidateInMandate
                  buttonAdd={false}
                  onChange={(val) => {
                    setFieldValue("fromParty", val?.party);
                    setFieldValue("memberId", val);
                  }}
                  value={values?.memberId}
                  error={Boolean(touched.memberId?.id && errors.memberId?.id)}
                  helperText={touched.memberId?.id && errors.memberId?.id}
                />
              </Grid>

              <Grid item xs={12}>
                <SelectCouncilPositions
                  dialogTitle={dialogTitle}
                  buttonAdd={true}

                  onChange={(val) => setFieldValue("positions", val)}
                  value={values?.positions}
                  error={Boolean(touched.positions?.id && errors.positions?.id)}
                  helperText={touched.positions?.id && errors.positions?.id}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_from_party")}
                </Typography>
                <BoxTextField value={values?.fromParty?.title || null} />

                {/* <SelectParty
                  disabled={true}
                  error={Boolean(touched.fromParty?.id && errors.fromParty?.id)}
                  helperText={touched.fromParty?.id && errors.fromParty?.id}
                  value={values?.fromParty}
                  buttonAdd={false}
                  onChange={(val) => setFieldValue("fromParty", val)}
                /> */}
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className={t("class-btn-create")}
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
